import React, { useMemo, useState } from "react";
import { Box, Flex, Text, BoxProps } from "@chakra-ui/react";
import { ProtectedImage } from "../../ProtectedImage";
import { useTranslation } from "react-i18next";
import { SharpCornerBox } from "./SharpCornerBox";
import { BundleItem } from "../../../types";

export interface BundleImageStackProps {
  items: BundleItem[];
  layout?: "grid" | "list" | "portrait";
  totalPages?: number;
  totalReleases?: number;
  totalBundleItems?: number;
  /**
   * When true, disables per-image hover interactions (translate lift,
   * brightness, z-index swap). Useful when the stack is part of a larger
   * clickable surface that should not hint at individual interactivity.
   */
  disableHover?: boolean;
  /**
   * When true, overlays each individual image with a dim so the "claimed"
   * state reads as dimming the covers rather than dimming the container
   * rectangle around the staggered stack.
   */
  claimedOverlay?: boolean;
}

// --- Constants & Configuration ---

// 1. Baseline pixel values
const BASE_IMAGE = { width: 107, height: 165 };
const GRID_CONTAINER_WIDTH = 308;
const GRID_CONTAINER_PADDING = 32; // Represents 16px on each side of the parent
const GRID_CONTENT_WIDTH = GRID_CONTAINER_WIDTH - GRID_CONTAINER_PADDING;
const REMAINING_CONTAINER_WIDTH = 60;
const GAP_IMAGES_TO_REMAINING = 8;
const LIST_IMAGE_MAX_WIDTH = 155;
const LIST_IMAGE_OFFSET = 12;

// Portrait layout: diagonal stagger filling a tall container
// Figma reference: 170px covers in 239px container, stagger 35px x / 61px y
const PORTRAIT_IMAGE_WIDTH_PCT = 71;  // 170/239 ≈ 71% of container width
const PORTRAIT_OFFSET_X_PCT = 15;     // 35/239 ≈ 15% horizontal shift per step
const PORTRAIT_OFFSET_Y_PCT = 16;     // 61/386 ≈ 16% vertical shift per step
const PORTRAIT_MAX_VISIBLE = 3;
const PORTRAIT_BORDER_RADIUS = 12;

// 2. Derived ratios and percentages for responsive scaling
const IMAGE_ASPECT_RATIO = BASE_IMAGE.width / BASE_IMAGE.height;
const GRID_CONTAINER_ASPECT_RATIO = GRID_CONTENT_WIDTH / BASE_IMAGE.height;
// All percentages are now relative to the GRID_CONTENT_WIDTH
const findRelativeValue = (px: number) => (px / GRID_CONTENT_WIDTH) * 100;

// Grid dimensions as percentages of the stack's container (which fills the content area)
const GRID_IMAGE_WIDTH_PCT = findRelativeValue(BASE_IMAGE.width);
const GRID_REMAINING_WIDTH_PCT = findRelativeValue(REMAINING_CONTAINER_WIDTH);
const GRID_GAP_PCT = findRelativeValue(GAP_IMAGES_TO_REMAINING);

// --- Helper Functions ---

/**
 * Calculates z-index to ensure correct stacking and hover effects.
 */
function computeZ(
  index: number,
  total: number,
  hoveredIndex: number | null,
): number {
  const BASE = 1;
  let position: number;

  if (hoveredIndex === null) {
    position = index;
  } else if (index === hoveredIndex) {
    position = 0;
  } else if (index < hoveredIndex) {
    position = hoveredIndex - index;
  } else {
    position = index;
  }

  return BASE + (total - position);
}

/** Inserts a placeholder card for the 2-image grid layout. */
function buildImagesWithPlaceholder(
  images: string[],
  isListLayout: boolean,
): (string | "placeholder")[] {
  if (!isListLayout && images.length === 2) {
    return [images[0], images[1], "placeholder-image"];
  }
  return images;
}

export function BundleImageStack({
  items,
  layout = "grid",
  totalPages,
  totalReleases,
  totalBundleItems,
  disableHover = false,
  claimedOverlay = false,
}: BundleImageStackProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const isListLayout = layout === "list";
  const isPortraitLayout = layout === "portrait";

  const releaseImages = useMemo(
    () => items.map((i) => i.thumbnail_url),
    [items],
  );

  const totalItems = totalBundleItems ?? items.length;

  const maxVisible = useMemo(() => {
    if (isPortraitLayout) {
      return PORTRAIT_MAX_VISIBLE;
    }
    // For list layout, it's always 3
    if (isListLayout) {
      return 3;
    }
    // For grid layout, apply the special case for exactly 5 items
    if (totalItems === 5) {
      return 3;
    }

    // Default for grid layout
    return 4;
  }, [isListLayout, isPortraitLayout, totalItems]);

  const imagesToShow = useMemo(
    () => releaseImages.slice(0, maxVisible),
    [releaseImages, maxVisible],
  );
  const remainingCount = Math.max(0, (totalBundleItems ?? releaseImages.length) - maxVisible);

  const shouldShowRemainingContainer = !isListLayout && !isPortraitLayout && remainingCount > 0;
  const displayImages = useMemo(
    () => buildImagesWithPlaceholder(imagesToShow, isListLayout || isPortraitLayout),
    [imagesToShow, isListLayout, isPortraitLayout],
  );
  const totalImages = displayImages.length;

  const processedImages = useMemo(() => {
    // A. List Layout Calculations
    const getListCardSize = (index: number) => {
      const steps =
        hoveredIndex !== null ? Math.abs(index - hoveredIndex) : index;
      const height = BASE_IMAGE.height - steps * LIST_IMAGE_OFFSET;
      return { width: height * IMAGE_ASPECT_RATIO, height };
    };

    const getListPositions = () => {
      if (totalImages <= 0) return [];
      const lastIndex = totalImages - 1;
      const { width: lastCardWidth } = getListCardSize(lastIndex);
      const availableSpace = LIST_IMAGE_MAX_WIDTH - lastCardWidth;
      const step = totalImages > 1 ? availableSpace / lastIndex : 0;
      return Array.from({ length: totalImages }, (_, i) => i * step);
    };

    // B. Grid Layout Calculations
    const getGridPositions = () => {
      if (totalImages <= 0) return [];
      if (shouldShowRemainingContainer) {
        const imagesAreaPct = 100 - GRID_REMAINING_WIDTH_PCT - GRID_GAP_PCT;
        const spacing =
          totalImages > 1
            ? (imagesAreaPct - GRID_IMAGE_WIDTH_PCT) / (totalImages - 1)
            : 0;
        return Array.from({ length: totalImages }, (_, i) => i * spacing);
      } else {
        const availableSpacing = 100 - GRID_IMAGE_WIDTH_PCT;
        const spacing =
          totalImages > 1 ? availableSpacing / (totalImages - 1) : 0;
        return Array.from({ length: totalImages }, (_, i) => i * spacing);
      }
    };

    // C. Portrait Layout Calculations — percentage-based vertical + horizontal stagger
    // Positions are reversed so the FIRST item sits at the bottom-right of the
    // stack (most prominent spot), with later items tucked up-and-left behind.
    // z-index still uses the array index so the first item stays on top.
    if (isPortraitLayout) {
      return displayImages.map((imageUrl, index) => {
        const isPlaceholder = imageUrl === "placeholder-image";
        const positionIndex = totalImages - 1 - index;
        const style: React.CSSProperties = {
          position: "absolute",
          width: `${PORTRAIT_IMAGE_WIDTH_PCT}%`,
          left: `${positionIndex * PORTRAIT_OFFSET_X_PCT}%`,
          top: `${positionIndex * PORTRAIT_OFFSET_Y_PCT}%`,
        };
        return {
          key: `${imageUrl}-${index}`,
          imageUrl,
          isPlaceholder,
          style,
          zIndex: computeZ(index, totalImages, hoveredIndex),
        };
      });
    }

    const positions = isListLayout ? getListPositions() : getGridPositions();

    // D. Map to a unified renderable data structure
    return displayImages.map((imageUrl, index) => {
      const isPlaceholder = imageUrl === "placeholder-image";
      const style: React.CSSProperties = { position: "absolute", top: 0 };

      if (isListLayout) {
        const { width, height } = getListCardSize(index);
        const steps =
          hoveredIndex !== null ? Math.abs(index - hoveredIndex) : index;
        style.width = `${width}px`;
        style.height = `${height}px`;
        style.left = `${positions[index]}px`;
        style.top = `${steps * LIST_IMAGE_OFFSET}px`;
      } else {
        style.width = `${GRID_IMAGE_WIDTH_PCT}%`;
        style.left = `${positions[index]}%`;
      }

      return {
        key: `${imageUrl}-${index}`,
        imageUrl,
        isPlaceholder,
        style,
        zIndex: computeZ(index, totalImages, hoveredIndex),
      };
    });
  }, [
    displayImages,
    isListLayout,
    isPortraitLayout,
    hoveredIndex,
    shouldShowRemainingContainer,
    totalImages,
  ]);

  // Define container props based on layout
  const containerProps: BoxProps = isPortraitLayout
    ? { width: "100%", height: "100%" }
    : isListLayout
      ? { width: `${LIST_IMAGE_MAX_WIDTH}px`, height: `${BASE_IMAGE.height}px` }
      : {
          width: "100%",
          aspectRatio: GRID_CONTAINER_ASPECT_RATIO,
        };

  const shadowGradient = isPortraitLayout
    ? "linear-gradient(135deg, transparent 60%, rgba(0,0,0,0.2) 80%, rgba(0,0,0,0.5) 100%)"
    : isListLayout
      ? "linear-gradient(135deg, transparent 70%, rgba(0,0,0,0.2) 85%, rgba(0,0,0,0.4) 100%)"
      : "linear-gradient(90deg, transparent 70%, rgba(0,0,0,0.2) 85%, rgba(0,0,0,0.4) 100%)";

  return (
    <Box position="relative" {...containerProps}>
      {processedImages.map(
        ({ key, imageUrl, isPlaceholder, style, zIndex }, index) => (
          <Box
            key={key}
            style={style}
            zIndex={zIndex}
            onMouseEnter={disableHover ? undefined : () => !isPlaceholder && setHoveredIndex(index)}
            onMouseLeave={disableHover ? undefined : () => !isPlaceholder && setHoveredIndex(null)}
            cursor={isPlaceholder ? "default" : disableHover ? "inherit" : "pointer"}
            _hover={!isPlaceholder && !disableHover ? { transform: "translateY(-4px)" } : {}}
            sx={{
              transition: disableHover
                ? undefined
                : "transform .2s ease-out, top .2s ease-out, left .2s ease-out, width .2s ease-out, height .2s ease-out, filter .2s ease-out, z-index 0s linear",
              filter: !disableHover && hoveredIndex === index ? "brightness(1.05)" : "none",
            }}
          >
            <Box
              w="100%"
              sx={{ aspectRatio: IMAGE_ASPECT_RATIO }}
              position="relative"
            >
              {isPlaceholder ? (
                <Box
                  w="100%"
                  h="100%"
                  bg="transparent.white.10"
                  borderRadius={isPortraitLayout ? PORTRAIT_BORDER_RADIUS : 8}
                  boxShadow={isPortraitLayout ? "-4px 4px 12px 0 rgba(0,0,0,0.5)" : "10px 0 20px 0 rgba(0,0,0,0.8)"}
                />
              ) : (
                <ProtectedImage
                  src={imageUrl}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  borderRadius={isPortraitLayout ? PORTRAIT_BORDER_RADIUS : 8}
                  border="1px solid"
                  borderColor="transparent.white.10"
                  alt={`Bundle release ${index + 1}`}
                  boxShadow={isPortraitLayout ? "-4px 4px 12px 0 rgba(0,0,0,0.5)" : "10px 0 20px 0 rgba(0,0,0,0.8)"}
                  pointerEvents="none"
                />
              )}
              {index < totalImages - 1 && (
                <Box
                  position="absolute"
                  inset={0}
                  borderRadius={isPortraitLayout ? PORTRAIT_BORDER_RADIUS : 8}
                  background={shadowGradient}
                  pointerEvents="none"
                />
              )}
              {claimedOverlay && !isPlaceholder && (
                <Box
                  position="absolute"
                  inset={0}
                  borderRadius={isPortraitLayout ? PORTRAIT_BORDER_RADIUS : 8}
                  bg="blackAlpha.500"
                  pointerEvents="none"
                />
              )}
            </Box>
          </Box>
        ),
      )}

      {shouldShowRemainingContainer && (
        <RemainingContainer remainingCount={remainingCount} />
      )}

      {isListLayout && totalPages != null && totalReleases != null && (
        <PagesReleasesInfo
          totalPages={totalPages}
          totalReleases={totalReleases}
        />
      )}
    </Box>
  );
}

// --- Sub-Components ---
function RemainingContainer({ remainingCount }: { remainingCount: number }) {
  return (
    <Box
      position="absolute"
      right={0}
      top="0"
      w={`${GRID_REMAINING_WIDTH_PCT}%`}
      h="100%"
      zIndex={0}
    >
      <Flex
        w="100%"
        h="100%"
        bg="transparent.white.10"
        borderRadius={10}
        align="center"
        justify="center"
        boxShadow="lg"
      >
        <Text color="neutral.400" fontSize="16px" fontWeight="500">
          +{remainingCount}
        </Text>
      </Flex>
    </Box>
  );
}

function PagesReleasesInfo({
  totalPages,
  totalReleases,
}: {
  totalPages: number;
  totalReleases: number;
}) {
  const { t } = useTranslation();

  return (
    <SharpCornerBox
      position="absolute"
      bottom="0"
      left="0"
      corners={["topLeft", "bottomRight"]}
      fontSize="10px"
      fontWeight="bold"
      background="black.80"
      color="neutral.200"
      px={2}
      py={1}
      zIndex={50}
      backdropFilter="blur(5px)"
      borderTopRightRadius="4px"
      borderBottomLeftRadius="8px"
    >
      {t("components.cards.bundleCard.pagesInReleases", {
        totalPages,
        totalReleases,
      })}
    </SharpCornerBox>
  );
}
